/**
 * Menu boards: predefined designs filled with items from the organization's menu library.
 *
 * Everything here is plain TypeScript with no runtime dependencies, because the same code runs
 * in three places that must agree to the pixel: the dashboard's live preview, the Electron
 * player and the Android player. The renderer produces an HTML string rather than touching the
 * DOM, so it also runs (and is tested) in Node.
 *
 * Menus are drawn on the device rather than shipped as pictures: text stays sharp at any
 * resolution, a price change is a few hundred bytes of manifest, and photos are cached like any
 * other media so the board keeps working offline.
 */
import { escapeHtml, safeColor, safeUrl } from './format.js';
import { menuFont } from './fonts.js';
import type { MenuPage } from './paginate.js';
import { EMPTY_BRAND, type MenuBoardContent, type MenuOrientation, type MenuSettings, type MenuTheme } from './types.js';
import { boldSections } from './templates/bold-sections.js';
import { cafePanels } from './templates/cafe-panels.js';
import { chalkboard } from './templates/chalkboard.js';
import { darkRibbon } from './templates/dark-ribbon.js';
import { blackGold } from './templates/black-gold.js';
import { comboDeals } from './templates/combo-deals.js';
import { kraftPaper } from './templates/kraft-paper.js';
import { plateGrid } from './templates/plate-grid.js';
import { retroDiner } from './templates/retro-diner.js';
import { sizeMatrix } from './templates/size-matrix.js';
import { splitHero } from './templates/split-hero.js';
import { trattoria } from './templates/trattoria.js';
import { woodFired } from './templates/wood-fired.js';
import { classicGrid } from './templates/classic-grid.js';
import { nightBitesColumns } from './templates/night-bites-columns.js';
import { nightBitesDuo } from './templates/night-bites-duo.js';
import { nightBitesSpotlight } from './templates/night-bites-spotlight.js';
import { pizzaShowGrid } from './templates/pizza-show-grid.js';
import { pizzaShowPanel } from './templates/pizza-show-panel.js';
import { pizzaShowHero } from './templates/pizza-show-hero.js';
import { foodCollageMosaic } from './templates/food-collage-mosaic.js';
import { foodCollageStrip } from './templates/food-collage-strip.js';
import { foodCollageColumns } from './templates/food-collage-columns.js';
import { brushBurgerBoard } from './templates/brush-burger-board.js';
import { brushBurgerCards } from './templates/brush-burger-cards.js';
import { brushBurgerHero } from './templates/brush-burger-hero.js';
import { stackPanelsFive } from './templates/stack-panels-five.js';
import { stackPanelsTrio } from './templates/stack-panels-trio.js';
import { stackPanelsBands } from './templates/stack-panels-bands.js';
import { freshDrinksCards } from './templates/fresh-drinks-cards.js';
import { freshDrinksGrid } from './templates/fresh-drinks-grid.js';
import { freshDrinksBanner } from './templates/fresh-drinks-banner.js';
import { chalkChiliTrio } from './templates/chalk-chili-trio.js';
import { chalkChiliHero } from './templates/chalk-chili-hero.js';
import { chalkChiliRings } from './templates/chalk-chili-rings.js';
import { walnutColumns } from './templates/walnut-columns.js';
import { walnutWall } from './templates/walnut-wall.js';
import { walnutCards } from './templates/walnut-cards.js';
import { tangerineSplit } from './templates/tangerine-split.js';
import { tangerineList } from './templates/tangerine-list.js';
import { tangerineBanner } from './templates/tangerine-banner.js';
import { inkWallPanels } from './templates/ink-wall-panels.js';
import { inkWallHero } from './templates/ink-wall-hero.js';
import { inkWallSizes } from './templates/ink-wall-sizes.js';
import { buildYourOwn } from './templates/build-your-own.js';
import type { MenuStyleVariant, MenuTemplate } from './templates/common.js';
import { CUSTOM_CSS, designMediaIds, renderCustomCanvas } from './custom.js';
import { blocksMediaIds, boardBlocks } from './blocks.js';
import { groupBySection } from './paginate.js';

export * from './types.js';
export * from './format.js';
export * from './fonts.js';
export { groupBySection, packColumns, type MenuBlock, type MenuColumn, type MenuPage } from './paginate.js';
export { MENU_CUISINES, type MenuCuisine, type MenuFamily, type MenuStyleVariant, type MenuTemplate } from './templates/common.js';
export { featuredItem } from './templates/cafe-panels.js';
export * from './custom.js';
export * from './blocks.js';
export * from './design-starters.js';
export {
  MENU_STARTERS,
  MENU_STARTER_PHOTOS,
  STARTER_PHOTO_PREFIX,
  starterBoard,
  starterItemContent,
  starterPhotoName,
  type MenuStarter,
  type StarterItem,
} from './starters.js';

export const MENU_TEMPLATES: MenuTemplate[] = [
  classicGrid,
  cafePanels,
  boldSections,
  chalkboard,
  darkRibbon,
  trattoria,
  woodFired,
  sizeMatrix,
  retroDiner,
  comboDeals,
  splitHero,
  kraftPaper,
  blackGold,
  plateGrid,
  // Matching sets: one look in three layouts, for the screens of one shop.
  nightBitesColumns,
  nightBitesDuo,
  nightBitesSpotlight,
  pizzaShowGrid,
  pizzaShowPanel,
  pizzaShowHero,
  foodCollageMosaic,
  foodCollageStrip,
  foodCollageColumns,
  brushBurgerBoard,
  brushBurgerCards,
  brushBurgerHero,
  stackPanelsFive,
  stackPanelsTrio,
  stackPanelsBands,
  freshDrinksCards,
  freshDrinksGrid,
  freshDrinksBanner,
  chalkChiliTrio,
  chalkChiliHero,
  chalkChiliRings,
  walnutColumns,
  walnutWall,
  walnutCards,
  tangerineSplit,
  tangerineList,
  tangerineBanner,
  inkWallPanels,
  inkWallHero,
  inkWallSizes,
  // Kept so boards made with it still draw and edit; no longer offered for new boards
  // (people make their own layout in "Your designs" instead): see LISTED_MENU_TEMPLATES.
  buildYourOwn,
];

/** The designs offered when creating or restyling a board ("Build your own" is not one of them). */
export const LISTED_MENU_TEMPLATES: MenuTemplate[] = MENU_TEMPLATES.filter((t) => t !== buildYourOwn);


export const DEFAULT_MENU_TEMPLATE_ID = classicGrid.id;

/**
 * Custom designs draw themselves from their own layout. They are not in MENU_TEMPLATES (the
 * gallery of predefined designs) but resolve like one.
 */
export const CUSTOM_TEMPLATE_ID = 'custom';

const customTemplate: MenuTemplate = {
  id: CUSTOM_TEMPLATE_ID,
  name: 'Custom design',
  description: 'A layout of your own, made in the design editor.',
  defaultTheme: classicGrid.defaultTheme,
  variants: [{ id: 'custom', name: 'Custom', theme: {} }],
  cuisines: [],
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },
  // One screen: a custom layout places its own lists, so there is nothing to page through.
  paginate: (board) => [
    { columns: [groupBySection(board).map((g) => ({ section: g.section, items: g.items, continued: false }))] },
  ],
  render: (ctx) =>
    ctx.board.custom
      ? renderCustomCanvas(ctx, ctx.board.custom)
      : '<div class="mb-empty">This board’s custom design is missing</div>',
  css: CUSTOM_CSS,
};

export function menuTemplate(id: string): MenuTemplate {
  if (id === CUSTOM_TEMPLATE_ID) return customTemplate;
  return MENU_TEMPLATES.find((t) => t.id === id) ?? classicGrid;
}

/** The board's style variant, or the design's first when it names none or an unknown one. */
export function menuVariant(template: MenuTemplate, id: string | null | undefined): MenuStyleVariant {
  return template.variants.find((v) => v.id === id) ?? template.variants[0]!;
}

/** A design's full theme in one of its variants: what picking that look applies. */
export function variantTheme(templateId: string, variantId?: string | null): MenuTheme {
  const template = menuTemplate(templateId);
  const variant = menuVariant(template, variantId);
  return { ...template.defaultTheme, ...variant.theme, variant: variant.id };
}

export const DEFAULT_MENU_SETTINGS: MenuSettings = {
  title: null,
  titleAr: null,
  language: 'both',
  currency: 'JOD',
  pageDurationMs: 15_000,
  showDescriptions: true,
  showIngredients: true,
};

/* --------------------------------------------------------------- compatibility */

/** The first player release that can draw menu boards. */
export const MENU_MIN_PLAYER_VERSION = '0.2.0';

/**
 * Whether a player can be sent menu content.
 *
 * A player validates its manifest against the schema it was built with, and a schedule whose
 * content type it does not know fails that validation: the player then refuses the whole
 * document and keeps playing stale content indefinitely. So menus are left out of manifests for
 * older players, which carry on with everything else, until they are upgraded.
 */
export function playerSupportsMenus(version: string | null | undefined): boolean {
  if (!version) return false;
  const parse = (v: string) => v.split(/[.+-]/).slice(0, 3).map((p) => Number.parseInt(p, 10) || 0);
  const have = parse(version);
  const need = parse(MENU_MIN_PLAYER_VERSION);
  for (let i = 0; i < 3; i += 1) {
    if (have[i]! !== need[i]!) return have[i]! > need[i]!;
  }
  return true;
}

/* ------------------------------------------------------------------------ media */

/** Every picture a board can show, so players cache them and the server sends them. */
export function menuMediaIds(
  board: Pick<MenuBoardContent, 'theme' | 'items' | 'sections' | 'brand' | 'decorMediaIds' | 'custom' | 'blocks'>,
): string[] {
  const ids = [
    board.theme.backgroundMediaId,
    board.brand?.logoMediaId,
    ...(board.decorMediaIds ?? []),
    ...(board.sections ?? []).map((s) => s.imageMediaId),
    ...board.items.map((i) => i.imageMediaId),
    ...designMediaIds(board.custom),
    ...blocksMediaIds(boardBlocks(board)),
  ];
  return [...new Set(ids.filter((id): id is string => Boolean(id)))];
}

/* ----------------------------------------------------------------- pagination */

/** Splits a board into the pages its design shows. Always at least one page. */
export function paginateMenu(board: MenuBoardContent, orientation: MenuOrientation): MenuPage[] {
  return menuTemplate(board.templateId).paginate(board, orientation);
}

/* --------------------------------------------------------------------- renderer */

export interface RenderMenuOptions {
  board: MenuBoardContent;
  /** Zero-based page to draw. */
  page: number;
  /** Size of the box the board fills, in CSS pixels. Everything scales from it. */
  width: number;
  height: number;
  /**
   * Orientation to paginate for. Players pass the screen's, so the page they were told to draw
   * is the page they draw even if the window is briefly another shape; the layout itself always
   * follows the box. Defaults to the box's orientation.
   */
  orientation?: MenuOrientation;
  /** URL for a cached image, or null when it is not available (it is then left out). */
  imageUrl: (mediaId: string) => string | null;
}

const BASE_CSS = `
.mb-root{position:absolute;inset:0;overflow:hidden;display:flex;flex-direction:column;
  background:var(--mb-bg);color:var(--mb-text);font-family:var(--mb-font-body);
  line-height:1.25;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}
.mb-root *{box-sizing:border-box;margin:0}
.mb-root .mb-bgimg{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0}
.mb-root>*{position:relative;z-index:1}
.mb-root>.mb-bgimg{position:absolute;z-index:0}
.mb-root .mb-ar{direction:rtl}
/* Arabic letters rise and descend further than Latin capitals; tight display leading clips them. */
.mb-root p.mb-ar,.mb-root span.mb-ar{line-height:1.3}
.mb-root .mb-footer{position:absolute;z-index:3;left:0;right:0;bottom:.9em;display:flex;justify-content:center;gap:.5em}
.mb-root .mb-dot{width:.6em;height:.6em;border-radius:50%;background:var(--mb-text);opacity:.3}
.mb-root .mb-dot.mb-on{opacity:1;background:var(--mb-accent)}
.mb-root .mb-empty{flex:1;display:flex;align-items:center;justify-content:center;font-size:1.6em;opacity:.6}
`;

/**
 * The stylesheet for every design. Theme values arrive as custom properties on each board's
 * root element, so two boards cross-fading on one screen never fight over a selector.
 */
export const MENU_CSS = BASE_CSS + MENU_TEMPLATES.map((t) => t.css).join('\n') + CUSTOM_CSS;

/**
 * Draws one page of a board as an HTML fragment (a root `<div>`; pair it with `MENU_CSS`, and
 * with `MENU_FONT_FACES` from `@signage/shared/menu-fonts` declared in the document).
 *
 * Sizes are in `em` from a base computed here, so the layout is identical on a 4K panel and in
 * a 400-pixel preview.
 */
export function renderMenuPage(options: RenderMenuOptions): string {
  const board = normalize(options.board);
  const { width, height } = options;
  const template = menuTemplate(board.templateId);
  const variant = menuVariant(template, board.theme.variant);
  const defaults = { ...template.defaultTheme, ...variant.theme };
  const theme = board.theme;
  const orientation: MenuOrientation = height > width ? 'portrait' : 'landscape';
  const pages = template.paginate(board, options.orientation ?? orientation);
  const pageIndex = Math.min(Math.max(0, options.page), pages.length - 1);

  // 16px on a 1920×1080 landscape screen (1080×1920 portrait), scaled to whatever box we fill.
  const reference = orientation === 'portrait' ? [1080, 1920] : [1920, 1080];
  // A custom design is laid out in canvas pixels, so one of its pixels is one em; it keeps its
  // own proportions and is letterboxed if the screen is another shape.
  const base =
    template.id === CUSTOM_TEMPLATE_ID && board.custom
      ? Math.min(width / board.custom.width, height / board.custom.height)
      : Math.max(4, 16 * Math.min(width / reference[0]!, height / reference[1]!));
  const font = menuFont(theme.fontId);

  const style = [
    `font-size:${base.toFixed(2)}px`,
    `--mb-bg:${safeColor(theme.backgroundColor, defaults.backgroundColor)}`,
    `--mb-surface:${safeColor(theme.surfaceColor, defaults.surfaceColor)}`,
    `--mb-text:${safeColor(theme.textColor, defaults.textColor)}`,
    `--mb-primary:${safeColor(theme.primaryColor, defaults.primaryColor)}`,
    `--mb-accent:${safeColor(theme.accentColor, defaults.accentColor)}`,
    `--mb-font-heading:${font.heading}`,
    `--mb-heading-weight:${font.headingWeight}`,
    `--mb-font-body:${font.body}`,
    `--mb-font-script:${font.script}`,
  ].join(';');

  const lang = board.settings.language;
  const background = theme.backgroundMediaId ? safeUrl(options.imageUrl(theme.backgroundMediaId)) : null;
  const body = template.render({
    board,
    page: pages[pageIndex] ?? { columns: [] },
    pageIndex,
    pageCount: pages.length,
    orientation,
    showEn: lang !== 'ar',
    showAr: lang !== 'en',
    img: (id) => (id ? options.imageUrl(id) : null),
  });
  const dots =
    pages.length > 1
      ? `<div class="mb-footer">${pages
          .map((_, i) => `<span class="mb-dot${i === pageIndex ? ' mb-on' : ''}"></span>`)
          .join('')}</div>`
      : '';

  return [
    `<div class="mb-root mb-t-${template.id} mb-v-${variant.id} mb-${orientation}${background ? ' mb-has-bg' : ''}"`,
    lang === 'ar' ? ' dir="rtl"' : '',
    ` style="${escapeHtml(style)}">`,
    background ? `<img class="mb-bgimg" src="${escapeHtml(background)}" alt="">` : '',
    body,
    dots,
    '</div>',
  ].join('');
}

/**
 * Fills fields a board written before they existed may lack, so every design can rely on them.
 */
function normalize(board: MenuBoardContent): MenuBoardContent {
  return {
    ...board,
    sections: board.sections ?? [],
    featuredItemId: board.featuredItemId ?? null,
    brand: { ...EMPTY_BRAND, ...(board.brand ?? {}) },
    decorMediaIds: board.decorMediaIds ?? [],
    items: board.items.map((item) => ({
      ...item,
      variants: item.variants ?? [],
      calories: item.calories ?? null,
      badge: item.badge ?? null,
      badgeAr: item.badgeAr ?? null,
      sectionId: item.sectionId ?? null,
    })),
  };
}
